/**
 * iter 72aj — Cash Register (Cash Book) report.
 *
 * Default view: per-bank register (like a bank register in QuickBooks) —
 * one row per cash movement with opening / running / closing balance.
 * Toggle "Show all banks" flips to consolidated: every wallet's register
 * stacked in one page.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, GitCompareArrows } from "lucide-react";
import { toast } from "sonner";
import { DateField, fmtISO, downloadStream, CsvButton } from "./_shared";
import { startOfMonth, endOfMonth } from "date-fns";

export function CashRegisterReport({ currency }) {
  const navigate = useNavigate();
  const [start, setStart] = useState(startOfMonth(new Date()));
  const [end, setEnd] = useState(endOfMonth(new Date()));
  const [wallets, setWallets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState(null);

  // Load wallets once so the picker can populate.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/accounts");
        setWallets(data || []);
        if (!selectedId && data && data.length) setSelectedId(data[0].id);
      } catch { /* ignore */ }
    })();
  }, [selectedId]);

  const load = useCallback(async () => {
    try {
      const params = { start: fmtISO(start), end: fmtISO(end) };
      if (!showAll && selectedId) params.account_id = selectedId;
      const { data } = await api.get("/reports/cash-register", { params });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load Cash Register");
    }
  }, [start, end, selectedId, showAll]);
  useEffect(() => { load(); }, [load]);

  const activeParams = () => {
    const p = { start: fmtISO(start), end: fmtISO(end) };
    if (!showAll && selectedId) p.account_id = selectedId;
    return p;
  };

  if (!data) return <div className="text-[var(--muted-text)] text-sm p-6">Loading…</div>;

  return (
    <div data-testid="cash-register-report" className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-end gap-3 flex-wrap">
          <DateField label="From" value={start} onChange={setStart} testId="cash-reg-start" />
          <DateField label="To" value={end} onChange={setEnd} testId="cash-reg-end" />
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wider text-[var(--muted-text)]">Bank</Label>
            <Select value={selectedId} onValueChange={setSelectedId} disabled={showAll}>
              <SelectTrigger data-testid="cash-reg-bank-select" className="w-56">
                <SelectValue placeholder="Pick bank" />
              </SelectTrigger>
              <SelectContent>
                {wallets.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name} <span className="text-[10px] text-[var(--muted-text)]">· {w.currency}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-[var(--muted-text)] px-2 py-1 rounded-full border border-[var(--divider)] h-9">
            <Switch checked={showAll} onCheckedChange={setShowAll}
                     data-testid="cash-reg-show-all-toggle" />
            <span>Show all banks</span>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <CsvButton testId="cash-reg-csv"
                      onClick={() => downloadStream("/reports/cash-register.csv",
                                                     activeParams(),
                                                     `cash_register_${fmtISO(start)}_${fmtISO(end)}.csv`)} />
          <Button data-testid="cash-reg-pdf-btn" variant="outline" size="sm"
                   className="rounded-full"
                   onClick={() => downloadStream("/reports/cash-register.pdf",
                                                  activeParams(),
                                                  `cash_register_${fmtISO(start)}_${fmtISO(end)}.pdf`)}>
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      <div data-testid="cash-reg-banks" className="space-y-5">
        {data.banks.length === 0 && (
          <div className="card-flat p-8 text-center text-[var(--muted-text)]">
            No cash movements in this window.
          </div>
        )}
        {data.banks.map((bank) => (
          <div key={bank.account_id} className="card-flat overflow-hidden"
                data-testid={`cash-reg-bank-${bank.account_id}`}>
            <div className="px-5 py-3 border-b border-[var(--divider)] flex items-center justify-between bg-[var(--hover)]/40">
              <div>
                <div className="font-semibold text-[var(--brand-text)]">{bank.account_name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)]">
                  {bank.currency} · {bank.rows.length} movement{bank.rows.length === 1 ? "" : "s"}
                </div>
              </div>
              <div className="flex gap-4 text-xs text-[var(--muted-text)] items-center">
                <div>
                  <div>Opening</div>
                  <div data-testid={`cash-reg-opening-${bank.account_id}`}
                        className="font-mono text-[var(--text)]">
                    {formatCurrency(bank.opening_balance, bank.currency)}
                  </div>
                </div>
                <div>
                  <div>Cash-in</div>
                  <div className="font-mono text-[#2F6B4A]">{formatCurrency(bank.cash_in, bank.currency)}</div>
                </div>
                <div>
                  <div>Cash-out</div>
                  <div className="font-mono text-[#B4283A]">{formatCurrency(bank.cash_out, bank.currency)}</div>
                </div>
                <div>
                  <div>Closing</div>
                  <div data-testid={`cash-reg-closing-${bank.account_id}`}
                        className="font-mono font-semibold text-[var(--text)]">
                    {formatCurrency(bank.closing_balance, bank.currency)}
                  </div>
                </div>
                {/* iter 72ak — deep-link to existing Bank Reconciliation
                    wizard with this bank pre-selected. Existing endpoints:
                    /reconciliation/session, /import-statement, /auto-match. */}
                <Button data-testid={`cash-reg-reconcile-${bank.account_id}`}
                         size="sm" variant="outline" className="rounded-full h-8 text-xs"
                         onClick={() => navigate(`/financials?tab=reconciliation&account=${bank.account_id}`)}>
                  <GitCompareArrows className="w-3.5 h-3.5 mr-1" /> Reconcile
                </Button>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                  <th className="py-2 pl-5 text-left w-24">Date</th>
                  <th className="py-2 text-left">Reference</th>
                  <th className="py-2 text-left">Description</th>
                  <th className="py-2 text-right w-28">Debit</th>
                  <th className="py-2 text-right w-28">Credit</th>
                  <th className="py-2 pr-5 text-right w-32">Running</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[var(--divider)] bg-[var(--hover)]/30">
                  <td className="py-1.5 pl-5 text-[10px] uppercase tracking-wider text-[var(--muted-text)]">Opening</td>
                  <td colSpan={4} />
                  <td className="py-1.5 pr-5 text-right font-mono">{formatCurrency(bank.opening_balance, bank.currency)}</td>
                </tr>
                {bank.rows.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--divider)] last:border-0"
                      data-testid={`cash-reg-row-${r.id}`}>
                    <td className="py-1.5 pl-5 text-xs">{r.date}</td>
                    <td className="py-1.5 text-xs font-mono">{(r.reference || "—").slice(0, 28)}</td>
                    <td className="py-1.5 text-xs text-[var(--muted-text)]">{(r.description || "").slice(0, 60)}</td>
                    <td className="py-1.5 text-right font-mono text-xs text-[#2F6B4A]">
                      {r.debit > 0 ? formatCurrency(r.debit, bank.currency) : ""}
                    </td>
                    <td className="py-1.5 text-right font-mono text-xs text-[#B4283A]">
                      {r.credit > 0 ? formatCurrency(r.credit, bank.currency) : ""}
                    </td>
                    <td className="py-1.5 pr-5 text-right font-mono text-xs">
                      {formatCurrency(r.running_balance, bank.currency)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[#2F4F4F] bg-[#F2ECE5] font-semibold">
                  <td className="py-2 pl-5 text-[10px] uppercase tracking-wider">Closing</td>
                  <td colSpan={2} />
                  <td className="py-2 text-right font-mono text-[#2F6B4A]">{formatCurrency(bank.cash_in, bank.currency)}</td>
                  <td className="py-2 text-right font-mono text-[#B4283A]">{formatCurrency(bank.cash_out, bank.currency)}</td>
                  <td className="py-2 pr-5 text-right font-mono">{formatCurrency(bank.closing_balance, bank.currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
